import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChatMessage } from '../interfaces/AiChat/ChatMessage';
import { ClientEvent } from '../interfaces/AiChat/ClientEvent';
import { ServerEvent } from '../interfaces/AiChat/ServerEvent';
import { getStoredToken } from '../utils/Auth';

function getWebSocketUrl() {
  return process.env.REACT_APP_AI_SERVER_URL || '';
}

export function useAiChat(chatId: string) {
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);

  const [connectionState, setConnectionState] = useState<
    'connecting' | 'open' | 'closed'
  >('connecting');

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [assistantStatus, setAssistantStatus] = useState<
    'idle' | 'thinking' | 'running_tool' | 'done'
  >('idle');
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const connect = useCallback(() => {
    const socket = new WebSocket(getWebSocketUrl());
    socketRef.current = socket;
    setConnectionState('connecting');
    setIsAuthenticated(false);

    socket.addEventListener('open', () => {
      setConnectionState('open');
      setError(null);

      const token = getStoredToken();

      if (!token) {
        setError('Authentication token not found');
        socket.close();
        return;
      }

      const authEvent: ClientEvent = {
        type: 'auth',
        token,
      };

      socket.send(JSON.stringify(authEvent));
    });

    socket.addEventListener('message', (event) => {
      try {
        const data = JSON.parse(event.data) as ServerEvent;

        switch (data.type) {
          case 'auth.ok':
            setIsAuthenticated(true);
            setError(null);
            return;

          case 'auth.error':
            setIsAuthenticated(false);
            setError(data.message);
            setAssistantStatus('idle');
            return;

          case 'chat.ack':
            return;

          case 'assistant.status':
            setAssistantStatus(data.status);
            return;

          case 'assistant.delta':
            setMessages((prev) => {
              const last = prev[prev.length - 1];

              if (last?.role === 'assistant') {
                return [
                  ...prev.slice(0, -1),
                  { ...last, text: last.text + data.text },
                ];
              }

              return [...prev, { role: 'assistant', text: data.text }];
            });
            return;

          case 'assistant.done':
            setAssistantStatus('done');
            return;

          case 'chat.error':
            setError(data.message);
            setAssistantStatus('idle');
            break;

          case 'chat.pong':
          default:
            break;
        }
      } catch {
        setError('Failed to parse server event');
      }
    });

    socket.addEventListener('close', () => {
      setConnectionState('closed');
      setIsAuthenticated(false);

      reconnectTimerRef.current = window.setTimeout(() => {
        connect();
      }, 1500);
    });

    socket.addEventListener('error', () => {
      setError('WebSocket connection error');
    });
  }, []);

  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
      }
      socketRef.current?.close();
    };
  }, [connect]);

  const sendEvent = useCallback((event: ClientEvent) => {
    const socket = socketRef.current;

    if (!socket || socket.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not connected');
    }

    socket.send(JSON.stringify(event));
  }, []);

  const sendMessage = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      if (!isAuthenticated) {
        setError('WebSocket is not authenticated yet');
        return;
      }

      setMessages((prev) => [
        ...prev,
        { role: 'user', text: trimmed },
        { role: 'assistant', text: '' },
      ]);
      setAssistantStatus('thinking');

      sendEvent({
        type: 'chat.message',
        chatId,
        message: trimmed,
      });
    },
    [chatId, isAuthenticated, sendEvent],
  );

  const ping = useCallback(() => {
    if (!isAuthenticated) {
      setError('WebSocket is not authenticated yet');
      return;
    }

    sendEvent({ type: 'chat.ping', chatId });
  }, [chatId, isAuthenticated, sendEvent]);

  return useMemo(
    () => ({
      connectionState,
      isAuthenticated,
      messages,
      assistantStatus,
      error,
      sendMessage,
      ping,
    }),
    [
      connectionState,
      isAuthenticated,
      messages,
      assistantStatus,
      error,
      sendMessage,
      ping,
    ],
  );
}
